import React, { useEffect, useState } from "react";
import styles from "./PurchaseOrderDetail.module.css";
import WebService from "../Services/WebService";
import { mediaUrl } from "../Services/mediaUrl";
import { useParams, useNavigate } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import Swal from "sweetalert2";
import AddPurchaseOrderPanel from "./AddPurchaseOrderPanel";

// --- Types ---
interface VendorAddress {
  complete_address?: string;
}
interface VendorDetails {
  company_name?: string;
  contact?: string;
  phone?: string;
  email?: string;
  type?: string;
  address?: VendorAddress[];
  mobile_number?: string;
}
interface OrderItem {
  purchase_order_item_id: number;
  product_id: number;
  name?: string;
  quantity: number;
  is_received: boolean;
  delivered_quantity?: number | string;
  purchase_price?: number | string;
  line_amount?: number | string;
  additional_charge?: number | null;
  additional_charge_note?: string | null;
  remarks?: string;
  category_id?: number;
  category_name?: string;
  measuring_unit?: string;
  measuring_unit_value?: string | number;
}
interface ApiResponse {
  purchase_order_details: {
    purchase_order_id: number;
    vendor_id: number;
    purchase_date: string;
    status: string;
    delivery_date: string;
    created_at: string;
    updated_at: string;
    deleted_at?: string | null;
    total_purchase_amount?: number | string;
    purchase_order_documents?: any;
  };
  vendor_details: VendorDetails;
  order_items: OrderItem[];
}
interface Material {
  id: number;
  name: string;
  ordered_quantity: number;
  delivered_quantity: number;
  is_received: boolean;
  product_id: number;
  purchase_price?: number | string;
  line_amount?: number | string;
  additional_charge?: number | null;
  additional_charge_note?: string | null;
  remarks?: string;
  category_id?: number;
  category_name?: string;
  measuring_unit?: string;
  measuring_unit_value?: string | number;
}

const PurchaseOrderDetail: React.FC = () => {
  const { purchase_order_id } = useParams<{ purchase_order_id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<ApiResponse["purchase_order_details"] | null>(null);
  const [vendor, setVendor] = useState<VendorDetails | null>(null);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [selectedMat, setSelectedMat] = useState<Material | null>(null);
  const [qtyEditMat, setQtyEditMat] = useState<Material | null>(null);
  const [showEditPanel, setShowEditPanel] = useState(false);

  const createdBy =
    order && "created_by_name" in order ? (order as any).created_by_name || "—" : "—";

  useEffect(() => {
    fetchOrderDetails();
    // eslint-disable-next-line
  }, [purchase_order_id]);

  const fetchOrderDetails = () => {
    setLoading(true);
    if (purchase_order_id) {
      WebService.getAPI({
        action: `/v1/door-management/purchase-order/${purchase_order_id}`,
      })
        .then((res: any) => {
          const data = res.data || {};
          const details =
            data.purchase_order_details ||
            (data.purchase_order_id
              ? {
                  purchase_order_id: data.purchase_order_id,
                  vendor_id: data.vendor_id,
                  purchase_date: data.purchase_date || data.created_at,
                  status: data.status,
                  delivery_date: data.delivery_date || data.expected_delivery_date,
                  created_at: data.created_at,
                  updated_at: data.updated_at,
                  deleted_at: data.deleted_at,
                  purchase_order_documents: data.purchase_order_documents,
                  created_by_name: data.created_by_name,
                  total_purchase_amount: data.total_purchase_amount,
                }
              : null);
          const vendorDetails =
            data.vendor_details ||
            (data.company_name || data.vendor_name
              ? {
                  company_name: data.company_name,
                  contact: data.vendor_name,
                  username: data.vendor_name,
                  mobile_number: data.mobile_number,
                  phone: data.phone,
                  email: data.email,
                  address: data.address,
                }
              : null);
          const orderItems = data.order_items || data.items || data.raw_materials || [];

          setOrder(details);
          setVendor(vendorDetails);
          setMaterials(
            orderItems.map((item: OrderItem) => ({
              id: item.purchase_order_item_id || (item as any).item_id || (item as any).id,
              name: item.name || (item as any).raw_material_name || "—",
              product_id: item.product_id,
              ordered_quantity: Number(item.quantity ?? 0),
              delivered_quantity: Number(item.delivered_quantity ?? 0),
              is_received: item.is_received ?? false,
              purchase_price: item.purchase_price,
              line_amount: item.line_amount,
              additional_charge: item.additional_charge,
              additional_charge_note: item.additional_charge_note,
              remarks: item.remarks || "",
              category_id: item.category_id,
              category_name: item.category_name,
              measuring_unit: item.measuring_unit,
              measuring_unit_value: item.measuring_unit_value,
            }))
          );
        })
        .catch(() => {
          setOrder(null);
          setVendor(null);
          setMaterials([]);
        })
        .finally(() => setLoading(false));
    }
  };

  const handleOrderStatusChange = (newStatus: "COMPLETED" | "CANCELED") => {
    const isCancel = newStatus === "CANCELED";
    const actionText = isCancel ? "Cancel" : "Complete";
    const warningText = isCancel
      ? "Are you sure you want to cancel this purchase order? This action cannot be undone."
      : "This will complete the order. Further edits won’t be allowed.";

    Swal.fire({
      title: `Are you sure?`,
      text: warningText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${actionText}`,
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#eee",
      reverseButtons: true,
      customClass: {
        confirmButton: styles.swalConfirmBtn,
        cancelButton: styles.swalCancelBtn,
        popup: styles.swalPopup,
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await WebService.patchAPI({
            action: `/v1/door-management/purchase-order/${order?.purchase_order_id}/status/${newStatus}`,
            body: {},
          });
          Swal.fire({
            icon: "success",
            title: `Order ${isCancel ? "Canceled" : "COMPLETED"}!`,
            text: isCancel
              ? "This purchase order has been canceled."
              : "This purchase order has been completed.",
            confirmButtonColor: "#FFD400",
            customClass: {
              confirmButton: styles.swalConfirmBtn,
              popup: styles.swalPopup,
            },
          });
          fetchOrderDetails();
        } catch (err: any) {
          Swal.fire({
            icon: "error",
            title: "Failed",
            text: err?.message || "Something went wrong.",
            confirmButtonColor: "#FFD400",
          });
        }
      }
    });
  };

  if (loading) {
    return (
      <div className={styles.loadingWrapper}>
        <div className={styles.loader}>Loading Purchase Order...</div>
      </div>
    );
  }
  if (!order) {
    return (
      <div className={styles.errorWrapper}>
        <div className={styles.notFound}>Purchase Order not found.</div>
      </div>
    );
  }

  const companyName = vendor?.company_name || "—";
  const vendorType = vendor?.type || "—";
  const contact = vendor?.contact || vendor?.mobile_number || "—";
  const phone = vendor?.phone || vendor?.mobile_number || "—";
  const email = vendor?.email || "—";
  const address =
    vendor?.address && vendor.address.length > 0 && vendor.address[0]?.complete_address
      ? vendor.address[0].complete_address
      : "—";

  const prettyStatus = (s?: string) => {
    if (!s) return "—";
    return s.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Normalize status to handle "IN PROGRESS" vs "IN_PROGRESS"
  const statusNorm = (order.status || "").replace(/\s+/g, "_").toUpperCase();

  const statusBadgeClass =
    styles.badge +
    " " +
    (statusNorm === "PENDING"
      ? styles.pending
      : statusNorm === "IN_PROGRESS"
      ? styles.inProgress
      : statusNorm === "DELIVERED"
      ? styles.delivered
      : statusNorm === "COMPLETED"
      ? styles.completed
      : statusNorm === "CANCELED"
      ? styles.canceled
      : "");

  const editPanelData = order
    ? {
        purchase_order_id: order.purchase_order_id,
        vendor_id: order.vendor_id,
        expected_delivery_date: order.delivery_date,
        raw_materials: (materials || []).map((m) => ({
          product_id: m.product_id,
          product_name: m.name,
          quantity: Number(m.ordered_quantity),
          purchase_price: m.purchase_price != null ? Number(m.purchase_price) : undefined,
          additional_charge: m.additional_charge,
          additional_charge_note: m.additional_charge_note,
          remarks: m.remarks,
          category_id: m.category_id,
          category_name: m.category_name,
          measuring_unit: m.measuring_unit,
          measuring_unit_value: m.measuring_unit_value,
        })),
      }
    : undefined;

  // ----- COLUMN SHOW/HIDE LOGIC -----
  const isPending = statusNorm === "PENDING";
  const isInProgress = statusNorm === "IN_PROGRESS";
  const isDelivered = statusNorm === "DELIVERED";
  const isCompletedOrCanceled = statusNorm === "COMPLETED" || statusNorm === "CANCELED";
  const canRecordDelivery = isPending || isInProgress || isDelivered;
  const canEditQuantity = !isCompletedOrCanceled;
  const showItemActions = canEditQuantity || canRecordDelivery;
  const showCancelBtn = isPending;
  const showCompleteBtn = isInProgress || isDelivered;

  // All items fully delivered?
  const allDelivered =
    materials.length > 0 &&
    materials.every(
      (m) => Number(m.delivered_quantity || 0) >= Number(m.ordered_quantity || 0)
    );

  const lineReceived = (mat: Material) => Number(mat.delivered_quantity || 0) > 0 || mat.is_received;
  const showNetQuantity = materials.some(lineReceived);
  const tableColCount = 5 + (showNetQuantity ? 1 : 0) + (showItemActions ? 1 : 0);

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.backRow}>
        <span className={styles.backIcon} onClick={() => navigate(-1)}>
          &larr; Purchase details
        </span>
      </div>

      {/* --- HEADER GRID --- */}
      <div className={styles.headerGrid}>
        <div className={styles.headerTopRow}>
          <div className={styles.headerTitle}>Purchase Order Details</div>
          {/* Show Edit (whole PO) only if pending */}
          {isPending && (
            <button
              className={styles.editBtn}
              style={{ minWidth: 0, padding: "7px 15px", fontSize: 16 }}
              onClick={() => setShowEditPanel(true)}
              title="Edit Purchase Order"
            >
              <FaEdit /> Edit
            </button>
          )}
        </div>
        <div className={styles.headerContentRow}>
          <div className={styles.headerLeft}>
            <div className={styles.infoGrid}>
              <div>
                <span className={styles.infoLabel}>Vendor Name</span>
                <span className={styles.infoValue}>{companyName}</span>
              </div>
              <div>
                <span className={styles.infoLabel}>Status</span>
                <span className={statusBadgeClass}>{prettyStatus(order.status)}</span>
              </div>
              <div>
                <span className={styles.infoLabel}>Order Date</span>
                <span className={styles.infoValue}>{formatDate(order.purchase_date)}</span>
              </div>
              <div>
                <span className={styles.infoLabel}>Expected Delivery</span>
                <span className={styles.infoValue}>{formatDate(order.delivery_date)}</span>
              </div>
              <div>
                <span className={styles.infoLabel}>Created By</span>
                <span className={styles.infoValue}>{createdBy}</span>
              </div>
              <div>
                <span className={styles.infoLabel}>PO ID</span>
                <span className={styles.infoValue}>{order.purchase_order_id || "—"}</span>
              </div>
              <div>
                <span className={styles.infoLabel}>Total Purchase Amount</span>
                <span className={styles.infoValue}>{formatMoney(order.total_purchase_amount)}</span>
              </div>
            </div>
          </div>
          <div className={styles.headerRight}>
            <div className={styles.vendorInfoTitleRow}>
              <span className={styles.vendorInfoTitle}>Vendor Information</span>
            </div>
            <div className={styles.vendorNameBig}>{companyName}</div>
            <div className={styles.vendorType}>{vendorType}</div>
            <div className={styles.vendorInfoGrid}>
              <span>
                <span className={styles.labelSmall}>Contact</span>:{" "}
                <span className={styles.valueSmall}>{contact}</span>
              </span>
              <span>
                <span className={styles.labelSmall}>Phone</span>:{" "}
                <span className={styles.valueSmall}>{phone}</span>
              </span>
              <span>
                <span className={styles.labelSmall}>Email</span>:{" "}
                <span className={styles.valueSmall}>{email}</span>
              </span>
              <span>
                <span className={styles.labelSmall}>Address</span>:{" "}
                <span className={styles.valueSmall}>{address}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* --- DOCUMENTS/IMAGES --- */}
      {order?.purchase_order_documents &&
        Array.isArray(order.purchase_order_documents) &&
        order.purchase_order_documents.length > 0 && (
          <div className={styles.poDocsSection}>
            <div className={styles.poDocsTitle}>PO Documents</div>
            <div className={styles.poDocsList}>
              {order.purchase_order_documents.map((doc: any) => (
                <a
                  key={doc.document_id}
                  href={mediaUrl(doc.file_name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.poDocThumbLink}
                  title="View Attachment"
                >
                  <img
                    src={mediaUrl(doc.file_name)}
                    alt="PO Document"
                    className={styles.poDocThumb}
                    loading="lazy"
                  />
                </a>
              ))}
            </div>
          </div>
        )}

      {/* --- MATERIALS ORDERED (TABLE) --- */}
      <div className={styles.materialsSection}>
        <div className={styles.materialsHeaderRow}>
          <span className={styles.materialsHeader}>Materials Ordered</span>
          <span className={styles.itemCountBadge}>{materials.length} Items</span>
        </div>
        <div className={styles.materialsTableWrap}>
          <table className={styles.materialsTable}>
            <thead>
              <tr>
                <th>Name</th>
                <th>New Order</th>
                <th>Quantity Delivered</th>
                {showNetQuantity && <th>Net Quantity</th>}
                <th>Purchase Price</th>
                <th>Amount</th>
                {showItemActions && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {materials.length === 0 ? (
                <tr>
                  <td colSpan={tableColCount} style={{ textAlign: "center" }}>
                    No items found.
                  </td>
                </tr>
              ) : (
                materials.map((mat) => (
                  <tr key={mat.id}>
                    <td>
                      {mat.name}
                      {!!Number(mat.additional_charge) && (
                        <div style={{ fontSize: 12, color: Number(mat.additional_charge) < 0 ? "#1e8542" : "#b45309" }}>
                          {mat.additional_charge_note || "Other charges"}: {Number(mat.additional_charge) < 0 ? "−" : "+"}
                          {formatMoney(Math.abs(Number(mat.additional_charge)))}
                        </div>
                      )}
                    </td>
                    <td>{formatQty(mat.ordered_quantity)}</td>
                    <td>{formatQty(mat.delivered_quantity)}</td>
                    {showNetQuantity && (
                      <td className={styles.netQuantityCell}>
                        {lineReceived(mat)
                          ? formatQty(Number(mat.ordered_quantity || 0) - Number(mat.delivered_quantity || 0))
                          : "—"}
                      </td>
                    )}
                    <td>{formatMoney(mat.purchase_price)}</td>
                    <td>{formatMoney(mat.line_amount)}</td>
                    {showItemActions && (
                      <td>
                        <div className={styles.itemActions}>
                          {canEditQuantity && (
                            <button
                              className={styles.qtyEditBtn}
                              onClick={() => setQtyEditMat(mat)}
                              title="Edit Quantity"
                            >
                              Edit Quantity
                            </button>
                          )}
                          {canRecordDelivery && (
                            <button
                              className={styles.editBtn}
                              style={{ minWidth: 0, padding: 7 }}
                              onClick={() => setSelectedMat(mat)}
                              title="Record Delivery"
                            >
                              <FaEdit />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
              {materials.length > 0 && (
                <tr>
                  <td colSpan={4 + (showNetQuantity ? 1 : 0)} style={{ textAlign: "right", fontWeight: 700 }}>
                    Total
                  </td>
                  <td style={{ fontWeight: 700 }}>{formatMoney(order.total_purchase_amount)}</td>
                  {showItemActions && <td />}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Complete / Cancel Buttons --- */}
      <div style={{ display: "flex", justifyContent: "center", margin: "42px 0 0 0", gap: 12 }}>
        { showCompleteBtn && (
          <button
            className={styles.markDeliveredBtn}
            onClick={() => handleOrderStatusChange("COMPLETED")}
            disabled={!allDelivered}
            title={allDelivered ? "Complete Order" : "Deliver all items to complete"}
          >
            Complete Order
          </button>
        )}
        { showCancelBtn && (
          <button
            className={styles.markDeliveredBtn}
            style={{ background: "#e53e3e", color: "#fff" }}
            onClick={() => handleOrderStatusChange("CANCELED")}
          >
            Cancel Order
          </button>
        )}
      </div>

      {/* ---- POPUP MODAL FOR DELIVERED ---- */}
      {selectedMat && (
        <MaterialDeliveredPopup
          material={selectedMat}
          onClose={() => setSelectedMat(null)}
          onDelivered={() => {
            setSelectedMat(null);
            fetchOrderDetails();
          }}
        />
      )}

      {qtyEditMat && (
        <EditQuantityPopup
          material={qtyEditMat}
          onClose={() => setQtyEditMat(null)}
          onUpdated={() => {
            setQtyEditMat(null);
            fetchOrderDetails();
          }}
        />
      )}

      {/* ---- EDIT PURCHASE ORDER PANEL ---- */}
      {showEditPanel && order && (
        <AddPurchaseOrderPanel
          open={showEditPanel}
          onClose={() => {
            setShowEditPanel(false);
            fetchOrderDetails();
          }}
          mode="edit"
          editData={editPanelData}
        />
      )}
    </div>
  );
};

interface QuantityPopupProps {
  material: Material;
  onClose: () => void;
  onUpdated: () => void;
}

const EditQuantityPopup: React.FC<QuantityPopupProps> = ({ material, onClose, onUpdated }) => {
  const [quantity, setQuantity] = useState<string>(String(material.ordered_quantity ?? ""));
  const [loading, setLoading] = useState(false);

  async function handleSave() {
    const newQty = Number(quantity);
    if (quantity.trim() === "" || !Number.isFinite(newQty) || newQty <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Quantity",
        text: "Quantity must be greater than 0.",
        confirmButtonColor: "#FFD400",
      });
      return;
    }

    setLoading(true);
    try {
      await WebService.patchAPI({
        action: `/v1/door-management/purchase-order/item/${material.id}/quantity`,
        body: { quantity: newQty },
      });
      Swal.fire({
        icon: "success",
        title: "Quantity updated",
        text: "Purchase order quantity has been corrected.",
        confirmButtonColor: "#FFD400",
        timer: 1200,
      });
      onClose();
      onUpdated();
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Failed to update quantity",
        text: err?.message || "Something went wrong.",
        confirmButtonColor: "#FFD400",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.materialModal}>
        <button className={styles.modalCloseBtn} onClick={onClose}>
          ×
        </button>
        <div className={styles.materialModalTitle}>Edit Quantity</div>
        <div className={styles.materialFormField}>
          <div className={styles.qtyMetaRow}>
            Item: <b>{material.name}</b>
          </div>
          <div className={styles.qtyMetaRow}>
            New Order: <b>{formatQty(material.ordered_quantity)}</b>
          </div>
          <div className={styles.qtyMetaRow}>
            Quantity delivered: <b>{formatQty(material.delivered_quantity)}</b>
          </div>
          {Number(material.delivered_quantity || 0) > 0 && (
            <div className={styles.qtyMetaRow}>
              Net Quantity:{" "}
              <b>
                {formatQty(
                  Number(material.ordered_quantity || 0) - Number(material.delivered_quantity || 0)
                )}
              </b>
            </div>
          )}
        </div>
        <div className={styles.materialFormField}>
          <label className={styles.materialFormLabel}>
            New quantity <span className={styles.requiredStar}>*</span>
          </label>
          <div className={styles.materialInputWrap}>
            <input
              className={styles.materialInput}
              placeholder="Enter quantity"
              value={quantity}
              type="number"
              min="0"
              step="any"
              onChange={(e) => setQuantity(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
        <div className={styles.materialModalFooter}>
          <button className={styles.materialCancelBtn} onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button className={styles.materialVerifyBtn} onClick={handleSave} disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Popup Component  ----
interface PopupProps {
  material: Material;
  onClose: () => void;
  onDelivered: () => void;
}

const MaterialDeliveredPopup: React.FC<PopupProps> = ({ material, onClose, onDelivered }) => {
  const ordered = Number(material.ordered_quantity || 0);
  const deliveredSoFar = Number(material.delivered_quantity || 0);
  const remaining = Math.max(ordered - deliveredSoFar, 0);

  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");
  const [units, setUnits] = useState<string>("");

  const [loading, setLoading] = useState(false);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files[0]) setFile(e.target.files[0]);
  }

  async function handleMarkAsDelivered() {
    const deliveryCount = Number(units);

    if (units === "" || !Number.isFinite(deliveryCount) || deliveryCount <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Quantity",
        text: "Please enter a quantity greater than 0.",
        confirmButtonColor: "#FFD400",
      });
      return;
    }

    if (deliveryCount > remaining) {
      Swal.fire({
        icon: "warning",
        title: "Too Many Units",
        text: `You can deliver at most ${formatQty(remaining)} more units for this item.`,
        confirmButtonColor: "#FFD400",
      });
      return;
    }

    setLoading(true);

    // form-data body
    const formData = new FormData();
    formData.append("delivery_count", String(deliveryCount));
    if (notes) formData.append("notes", notes);
    if (file) formData.append("image", file);

    try {
      await WebService.putAPI({
        action: `/v1/door-management/purchase-order/item/${material.id}/delivery-update`,
        body: formData,
        isFormData: true,
      });

      Swal.fire({
        icon: "success",
        title: "Delivery Updated!",
        text: "Material marked as delivered.",
        confirmButtonColor: "#FFD400",
        timer: 1200,
      });

      onClose();
      onDelivered();
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Failed to update delivery",
        text: err?.message || "Something went wrong.",
        confirmButtonColor: "#FFD400",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.materialModal}>
        <button className={styles.modalCloseBtn} onClick={onClose}>
          ×
        </button>
        <div className={styles.materialModalTitle}>Material verification</div>

        {/* Update Units */}
        <div className={styles.materialFormField}>
          <label className={styles.materialFormLabel}>
            Add delivered units <span className={styles.requiredStar}>*</span>
          </label>
          <div className={styles.materialInputWrap}>
            <input
  className={styles.materialInput}
  placeholder="Enter delivered units"
  value={units}
  type="number"
  onChange={(e) => setUnits(e.target.value)}
  disabled={loading}
/>

            <span className={styles.materialEditIcon}>
              <FaEdit />
            </span>
          </div>
          <div className={styles.helperRow}>
            Remaining: <b>{formatQty(remaining)}</b> units
          </div>
        </div>

        {/* Upload Delivery Proof */}
        <div className={styles.materialFormField}>
          <label className={styles.materialFormLabel}>Upload Delivery Proof</label>
          <div className={styles.materialUploadArea}>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={handleFile}
              disabled={loading}
            />
            Tap to upload or drag and drop the image
          </div>
          {file && (
            <div className={styles.materialThumbsRow}>
              <div className={styles.materialThumb}>
                <img src={URL.createObjectURL(file)} alt="Proof" />
              </div>
            </div>
          )}
        </div>

        {/* Condition Notes */}
        <div className={styles.materialFormField}>
          <label className={styles.materialFormLabel}>Condition Notes</label>
          <textarea
            className={styles.materialTextarea}
            placeholder="Optional notes about condition..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={loading}
          />
        </div>
        <div className={styles.materialModalFooter}>
          <button className={styles.materialCancelBtn} onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className={styles.materialVerifyBtn}
            onClick={handleMarkAsDelivered}
            disabled={loading || remaining <= 0}
          >
            {loading ? "Verifying..." : "Verify"}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Utilities ---
function formatQty(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "0";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("en-IN", { maximumFractionDigits: 3 });
}

function formatMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "—";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(d: string | undefined) {
  if (!d) return "Not Set";
  if (d.startsWith("0001-01-01")) return "Not Set";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "Not Set";
  return dt.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

export default PurchaseOrderDetail;
